// Which of the two fleets this phone belongs to, and which physical handset it is.
//
// The same app runs in two places now: the iPhones open it from the home screen, the
// Android phones run it inside an APK. They fail differently — a stale web app is a
// service worker problem, a stale APK is an install problem — so a support request has
// to say which one it came from. The user agent alone does not: an Android web view
// looks close enough to Chrome to be read as the web app.
//
// The APK can also answer a question the web app cannot: which handset this is. A phone
// is only ever named by its database label ("Device 14"), and that label maps to nothing
// anybody can pick off a shelf. The serial does, and Knox Manage hands it to the app.
//
// Resolved once and held in shared state, because the places that need it read a value
// rather than make a call.
export type ScanPlatform = 'android-native' | 'web'

export interface NativeInfo {
  platform: ScanPlatform
  // The APK's own version, as Android knows it: "1.0 (1)". Null on the web, where the
  // build is already named by appVersion.
  build: string | null

  // The hardware, as Knox Manage knows it. Null everywhere except a managed APK, and
  // that is the whole plain-browser guard: nothing downstream has to test for a
  // platform, because a phone with nothing to say says null and is left out.
  //
  // The serial is the one that matters. It is what the Knox Manage console shows and
  // what is printed on the handset, so it is the value that turns a Slack message into
  // a phone somebody can walk to.
  serial: string | null
  deviceName: string | null
  model: string | null
  // Android's own identifier for this app on this phone. Not the serial and no
  // substitute for it, but it needs no Knox Manage configuration at all and it survives
  // both a reinstall of the same-signed APK and a phone re-provisioned under a new
  // label. That makes it what we have left if the managed configuration is ever missing.
  nativeDeviceId: string | null
}

const emptyNativeInfo = (): NativeInfo => ({
  platform: 'web',
  build: null,
  serial: null,
  deviceName: null,
  model: null,
  nativeDeviceId: null,
})

// Asked for once for the whole app. Several screens read this, and the native calls
// behind it never change their answer while the app is open.
let asked = false

// Settled when those calls have finished, so a caller that must not read half an answer
// can wait for the whole one. Diagnostics are built the moment the help button is
// pressed and identity is reported the moment the app opens — both can happen before the
// bridge has replied, and a support request naming no phone is the exact thing this was
// written to stop.
let resolved: Promise<void> | null = null

// How long anything will wait for those calls before carrying on without them.
//
// Bounded for the same reason SUPPORT_TIMEOUT_MS is, and with the same button at stake.
// diagnostics() waits on this, send() waits on diagnostics(), and the support modal holds
// "Sending" with the button disabled until send() answers — so an unbounded wait here is
// a help button that sticks for ever and never even reaches the offline queue that was
// meant to catch it. That would apply to the iPhones too, which gain nothing from any of
// this.
//
// A failed import rejects, and that case is already handled below. This is for the one
// that neither resolves nor rejects: a chunk fetch that venue WiFi accepts and then never
// answers, which is the condition the whole app is written around. A phone that has not
// answered in a second and a half is treated as a phone with nothing to say, which is
// what an iPhone is anyway.
const NATIVE_INFO_TIMEOUT_MS = 1500

/**
 * Waits for the native lookups to finish, or gives up on them. Resolves immediately on
 * the web, and never rejects: a phone that cannot answer has nothing to add, which is not
 * a failure.
 *
 * Only ever as good as the kickoff in useNativeInfo, which has to have been called for
 * there to be anything to wait for. Every caller here reads the composable first.
 */
export const nativeInfoReady = (): Promise<void> => Promise.race([
  resolved ?? Promise.resolve(),
  new Promise<void>(resolve => setTimeout(resolve, NATIVE_INFO_TIMEOUT_MS)),
])

/**
 * The identity fields as the API takes them, with anything unknown left out entirely.
 *
 * Omitted rather than sent as null, and that is the point rather than tidiness. The
 * backend must never blank out a serial it already holds because a phone happened not to
 * report one this time, so "absent" has to mean "nothing to say" and never "no serial".
 * The iPhones therefore send an empty object here, and a phone whose managed
 * configuration has not landed yet sends only what it does know.
 *
 * Shared by the support request and the identity report so the two cannot drift into
 * describing the same phone under different names.
 */
export const identityPayload = (info: NativeInfo): Record<string, string> => {
  const fields: Record<string, string | null> = {
    serial: info.serial,
    device_name: info.deviceName,
    model: info.model,
    native_device_id: info.nativeDeviceId,
  }

  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => !!value),
  ) as Record<string, string>
}

// Whatever Knox Manage pushed, keyed exactly as the console set it. Typed loosely on
// purpose, to match a plugin that passes every key through rather than only the two
// declared in app_restrictions.xml.
interface ScanIdentityPlugin {
  get(): Promise<Record<string, string>>
}

// A key that ends in a serial-ish word: `serial`, `device_serial`, `serial_number`,
// `serialNumber`.
//
// Anchored to the end deliberately. A bare /serial/i also matches something like
// `serial_config_version`, and reporting a schema version as a phone's serial is worse
// than reporting none: the Slack line would look answered and send somebody to a shelf
// looking for a handset that does not exist.
const SERIAL_KEY = /serial(_?number|no)?$/i

/**
 * The serial out of whatever Knox Manage actually sent.
 *
 * The declared key first, which is what app_restrictions.xml puts on the KM console and
 * therefore what arrives in the ordinary case. Anything else serial-looking after that,
 * because a device policy controller is free to send keys nobody declared, and which
 * wildcards KM exposes for an internally uploaded APK is answered in that console rather
 * than in this repository.
 *
 * This is the half that makes the plugin's pass-everything-through worth having. On its
 * own, an undeclared key reached the web layer and was then dropped one line later —
 * a new bundle and a new APK across twenty phones to fix, rather than the console change
 * it should be.
 */
export const serialFrom = (identity: Record<string, string>): string | null => {
  // Emptiness is tested per key rather than once at the top. An enrolled phone whose
  // `serial` was never filled in reads it back as blank, and that blank still matches the
  // pattern - so searching by name alone finds the declared key, returns nothing, and
  // hides a real value sitting under another name.
  const usable = (key: string): string | null => identity[key]?.trim() || null

  return usable('serial')
    ?? Object.keys(identity)
      .filter(key => SERIAL_KEY.test(key))
      .map(usable)
      .find(value => value !== null)
    ?? null
}

/**
 * Fills in everything only an APK can answer, leaving the web app as it was built.
 *
 * Each source is asked inside its own try because they fail independently: a phone that
 * was never enrolled has no managed configuration at all, which is normal, and neither
 * that nor a missing plugin is a reason to leave the rest of a support request blank.
 */
const loadNativeInfo = async (info: Ref<NativeInfo>): Promise<void> => {
  // Imported here rather than at the top of the file so the web build does not pull
  // the native bridge into its bundle for an answer it already knows.
  const { Capacitor, registerPlugin } = await import('@capacitor/core')

  if (!Capacitor.isNativePlatform()) {
    return
  }

  info.value.platform = 'android-native'

  try {
    const { App } = await import('@capacitor/app')
    const { version, build } = await App.getInfo()

    info.value.build = `${version} (${build})`
  } catch {
    // Knowing it is the APK is most of the value. A missing version number is not
    // worth failing over.
  }

  try {
    const identity = await registerPlugin<ScanIdentityPlugin>('ScanIdentity').get()

    // Read by the key the configuration sets, or anything else serial-looking rather
    // than dropped — see serialFrom.
    info.value.serial = serialFrom(identity)
    // Both spellings, because which one Knox Manage writes is settled in its console
    // rather than here.
    info.value.nativeDeviceId = identity.device_id ?? identity.deviceId ?? null
  } catch {
    // No managed configuration, or a build without the plugin. The device id below
    // still leaves us something stable to recognise the phone by.
  }

  try {
    const { Device } = await import('@capacitor/device')
    const { name, model } = await Device.getInfo()

    info.value.deviceName = name ?? null
    info.value.model = model ?? null

    // Only where Knox Manage said nothing. Its value is the one that matches the
    // console, and overwriting it with Android's would defeat the point of asking.
    if (!info.value.nativeDeviceId) {
      info.value.nativeDeviceId = (await Device.getId()).identifier ?? null
    }
  } catch {
    // The model is a convenience. Its absence costs nothing the serial does not
    // already cover.
  }
}

export const useNativeInfo = () => {
  const info = useState<NativeInfo>('native-info', emptyNativeInfo)

  if (import.meta.client && !asked) {
    asked = true

    resolved = loadNativeInfo(info).catch(() => {
      // No bridge at all, which is the web app.
    })
  }

  return info
}
